/**
 * Wireless access-point catalog — Wi-Fi 6 indoor + outdoor APs from
 * the five vendors that own the enterprise SMB / campus Wi-Fi market.
 *
 *   TP-Link Omada — SMB-focused, cloud-managed via Omada SDN
 *   Ubiquiti UniFi — prosumer / SMB, cloud-managed via UniFi
 *   Cisco Meraki — enterprise + outdoor industrial, cloud-managed
 *   HPE Aruba Instant On — SMB plug-and-play, cloud-managed
 *   Cambium cnPilot — outdoor / transportation hardened
 *
 * APs are placed via the `wireless-access-point` EquipmentType (already
 * existed in equipmentTypes.ts since the early CCTV designer days but
 * had no catalog backing). They're `isSwitchableDevice = true` because
 * most enterprise APs have a small built-in switch on 1-4 LAN ports
 * — cameras CAN terminate there in edge deployments, though dedicated
 * switches remain the cleaner pattern.
 *
 * Compiled: 2026-05-15, release/v1.6.7-23k-catalog.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const WIRELESS_ACCESS_POINTS: EquipmentCatalogEntry[] = [
  // ═══════════════════════════════════════════════════════════════════════
  // TP-Link Omada — SMB outdoor Wi-Fi 6
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.omadanetworks.com/us/business-networking/omada-wifi-outdoor/eap650-outdoor/
  // SRC: https://flyteccomputers.com/tp-link-eap650-outdoor
  // SRC: https://www.amazon.com/TP-Link-EAP650-Outdoor-Seamless-Multiple-Controller/dp/B0C2JQ15P3
  //      EAP650-Outdoor: Wi-Fi 6 AX3000 indoor/outdoor dual-band
  //      (2402 Mbps @ 5 GHz + 574 Mbps @ 2.4 GHz = 2976 Mbps combined),
  //      OFDMA + MU-MIMO, 1× GbE RJ45, 802.3at PoE+ or passive PoE
  //      (adapter included), IP67 weatherproof, Omada Mesh support,
  //      coverage up to 3500 sq ft per AP, Starlink-compatible.
  {
    id: 'tplink-eap650-outdoor',
    manufacturer: 'TP-Link',
    model: 'Omada EAP650-Outdoor',
    category: 'wireless-access-point',
    tagline:
      'EAP650-Outdoor — Wi-Fi 6 AX3000 outdoor, IP67, 802.3at PoE, Omada Mesh, 3500 sq ft',
    verified: true,
    datasheetUrl:
      'https://www.omadanetworks.com/us/business-networking/omada-wifi-outdoor/eap650-outdoor/',
    specs: {
      wifiStandard: 'wifi6',
      bands: ['2.4 GHz', '5 GHz'],
      throughputMbps: 2976, // 2402 + 574
      spatialStreams: '2+2',
      muMimoOfdma: true,
      lanPorts: 0, // single PoE-in port doubles as uplink, no additional LAN
      uplinkSpeed: '1G',
      poeIn: '802.3at',
      outdoor: true,
      ipRating: 'IP67',
      meshCapable: true,
      cloudPlatform: 'Omada SDN',
      clientCoverageM: 100, // ~3500 sq ft → ~325 sq m → 100 m omni LoS
      // SRC: https://static.tp-link.com/upload/product-overview/2025/202508/20250806/Datasheet_EAP650-Outdoor%20V1.pdf
      //      Antenna gain 4 dBi @ 2.4 GHz, 5 dBi @ 5 GHz. Transmit power is
      //      quoted per region as EIRP: CE < 20 dBm @ 2.4 GHz, < 30 dBm
      //      @ 5 GHz (FCC < 25 dBm both bands). Stored as the CE figures;
      //      the propagation model clamps to the active regulatory cap.
      antennaGain24Dbi: 4,
      antennaGain5Dbi: 5,
      eirp24Dbm: 20,
      eirp5Dbm: 30,
      // "2x internal dual-band omni antennas"; the datasheet title itself
      // reads "Omada Omnidirectional AX3000 Wi-Fi 6 Indoor/Outdoor AP".
      antennaPattern: 'omni',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Ubiquiti UniFi — outdoor mesh
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://techspecs.ui.com/unifi/wifi/u6-mesh?subcategory=wifi-outdoor
  // SRC: https://flyteccomputers.com/ubiquiti-u6-mesh-us
  // SRC: https://www.bhphotovideo.com/c/product/1706147-REG/ubiquiti_networks_u6_mesh_us_u6_mesh_wifi_6.html
  //      UniFi U6 Mesh: Wi-Fi 6 dual-band 4×4 (5 GHz) + 2×2 (2.4 GHz)
  //      MU-MIMO, 4.8 Gbps @ 5 GHz BW160 + 573.5 Mbps @ 2.4 GHz BW40.
  //      6 total spatial streams. GbE uplink (PoE), IPX5 weather-
  //      resistant for outdoor installs, -22..+140 °F (-30..+60 °C)
  //      operating range, 140 m² (1500 ft²) coverage, 250+ concurrent
  //      clients. Wall / pole mounting kit included.
  {
    id: 'ubiquiti-unifi-u6-mesh',
    manufacturer: 'Ubiquiti',
    model: 'UniFi U6 Mesh',
    category: 'wireless-access-point',
    tagline:
      'UniFi U6 Mesh — Wi-Fi 6 indoor/outdoor, 4×4 5 GHz + 2×2 2.4 GHz, IPX5, 250+ clients',
    verified: true,
    datasheetUrl: 'https://techspecs.ui.com/unifi/wifi/u6-mesh?subcategory=wifi-outdoor',
    specs: {
      wifiStandard: 'wifi6',
      bands: ['2.4 GHz', '5 GHz'],
      throughputMbps: 5374, // 4800 + 574
      spatialStreams: '2+4',
      muMimoOfdma: true,
      lanPorts: 0,
      uplinkSpeed: '1G',
      poeIn: '802.3af', // PoE adapter included; supports 802.3af
      outdoor: true,
      ipRating: 'IPX5',
      operatingTempC: '-30..+60',
      maxClients: 250,
      meshCapable: true,
      cloudPlatform: 'UniFi Network',
      clientCoverageM: 70, // 140 m² → ~7 m radius, but outdoor LoS extends ~70 m
      // SRC: https://techspecs.ui.com/unifi/wifi/u6-mesh
      //      Max TX power 22 dBm @ 2.4 GHz, 26 dBm @ 5 GHz (conducted);
      //      antenna gain 3 dBi @ 2.4 GHz, 5 dBi @ 5 GHz.
      txPower24Dbm: 22,
      txPower5Dbm: 26,
      antennaGain24Dbi: 3,
      antennaGain5Dbi: 5,
      // Vertical cylinder body (48.5 x 159.5 mm, u6-mesh_ds.pdf), pole/wall/
      // desk mounts; Ubiquiti documents 360-degree omnidirectional coverage.
      antennaPattern: 'omni',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Cisco Meraki — enterprise outdoor / industrial
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.cisco.com/site/us/en/products/networking/wireless/access-points/meraki-access-points/mr76.html
  // SRC: https://documentation.meraki.com/MR/MR_Overview_and_Specifications/MR76_Datasheet
  // SRC: https://www.bhphotovideo.com/c/product/1699524-REG/cisco_mr76_hw_meraki_mr76_wi_fi_6.html
  //      Meraki MR76: Wi-Fi 6 dual-band (2.4 + 5 GHz) 2×2:2 MU-MIMO,
  //      max 1.7 Gbps, external N-type antennas (sold separately —
  //      gives the installer flexibility for directional / omni /
  //      sector based on the deployment), 1× GbE PoE-in (802.3af),
  //      IP67 outdoor + vibration + shock tested, -40..+65 °C op
  //      temperature, dedicated 3rd radio for WIDS/WIPS scanning +
  //      4th integrated Bluetooth radio. Cloud-managed via Meraki
  //      Dashboard (license required).
  {
    id: 'cisco-meraki-mr76',
    manufacturer: 'Cisco Meraki',
    model: 'MR76',
    category: 'wireless-access-point',
    tagline:
      'Meraki MR76 — Wi-Fi 6 outdoor/industrial, external N-type antennas, IP67, -40..+65 °C',
    verified: true,
    datasheetUrl:
      'https://documentation.meraki.com/MR/MR_Overview_and_Specifications/MR76_Datasheet',
    specs: {
      wifiStandard: 'wifi6',
      bands: ['2.4 GHz', '5 GHz'],
      throughputMbps: 1700,
      spatialStreams: '2+2',
      muMimoOfdma: true,
      lanPorts: 0,
      uplinkSpeed: '1G',
      poeIn: '802.3af',
      outdoor: true,
      ipRating: 'IP67',
      operatingTempC: '-40..+65',
      cloudPlatform: 'Meraki Dashboard',
      clientCoverageM: 100,
      // SRC: https://documentation.meraki.com/Wireless/Product_Information/Overviews_and_Datasheets/MR76_Datasheet
      //      Total TX power (conducted) 20.0 dBm on both bands per the RF
      //      performance tables. Antennas are EXTERNAL (sold separately);
      //      the gains stored here are the vendor's standard dual-band omni
      //      set MA-ANT-20 (4 dBi @ 2.4 GHz, 7 dBi @ 5 GHz):
      // SRC: https://meraki.cisco.com/wp-content/uploads/2020/05/meraki_datasheet_antenna_omni_4_7dBi.pdf
      txPower24Dbm: 20,
      txPower5Dbm: 20,
      antennaGain24Dbi: 4,
      antennaGain5Dbi: 7,
      // Follows the assumed MA-ANT-20 kit, which is an omni set by name.
      antennaPattern: 'omni',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // HPE Aruba Instant On — SMB indoor Wi-Fi 6
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://instant-on.hpe.com/products/access-points/access-point-22/
  // SRC: https://www.bhphotovideo.com/c/product/1811349-REG/aruba_s1u75a_hpe_networking_instant_on.html
  // SRC: https://www.amazon.com/Aruba-Wireless-Power-Not-Included/dp/B0CK7VKRG3
  //      Instant On AP22D: Wi-Fi 6 (802.11ax) 2×2 dual-band, up to
  //      1.7 Gbps. INDOOR ONLY (small office / retail). UNIQUE: 4×
  //      Gigabit Ethernet LAN ports (excluding the 2.5 GbE PoE
  //      uplink) — turns the AP into a small wired switch for IP
  //      phones / printers / cameras, with 2 ports providing 15 W
  //      PoE-out (802.3af). 75+ concurrent clients. OFDMA + MU-MIMO.
  //      Wall- or desk-mount.
  {
    id: 'aruba-instant-on-ap22d',
    manufacturer: 'HPE Aruba',
    model: 'Instant On AP22D',
    category: 'wireless-access-point',
    tagline:
      'Instant On AP22D — Wi-Fi 6 indoor + 4× GbE LAN + 2× PoE-out, 2.5G uplink, 75+ clients',
    verified: true,
    datasheetUrl: 'https://instant-on.hpe.com/products/access-points/access-point-22/',
    specs: {
      wifiStandard: 'wifi6',
      bands: ['2.4 GHz', '5 GHz'],
      throughputMbps: 1700,
      spatialStreams: '2+2',
      muMimoOfdma: true,
      lanPorts: 4, // unique: 4× GbE downstream LAN (2 with PoE-out)
      uplinkSpeed: '2.5G',
      poeIn: '802.3at',
      outdoor: false,
      maxClients: 75,
      cloudPlatform: 'Aruba Instant On',
      // SRC: https://tapeandmedia.com/content/HPE-Aruba/HPE-Networking-Instant-On-Access-Point-Dual-Radio-2x2-Wi-Fi-6-(RW1)-AP22D-S1U75A-digital-data-sheet-.pdf
      //      (HPE digital data sheet.) Max transmit power +20 dBm @ 2.4 GHz
      //      and +21 dBm @ 5 GHz (per-chain +17/+18 dBm plus 3 dB MIMO
      //      gain, i.e. aggregate conducted). Dual semi-directional 2x2
      //      antennas, 5.2 dBi @ 2.4 GHz, 5.4 dBi @ 5 GHz.
      txPower24Dbm: 20,
      txPower5Dbm: 21,
      antennaGain24Dbi: 5.2,
      antennaGain5Dbi: 5.4,
      // "Dual semi-directional 2x2 antennas" per the HPE data sheet.
      antennaPattern: 'semi-directional',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Cambium cnPilot — transportation-hardened outdoor
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.cambiumnetworks.com/products/wifi/cnpilot-e510-wifi-access-point/
  // SRC: https://brandcentral.cambiumnetworks.com/m/55cbbf172867113c/original/Cambium_Networks_data_sheet_cnPilot_e505_e510_e700_family.pdf
  //      cnPilot e510 Outdoor: Wi-Fi 5 (802.11ac wave 2) 2×2:2 MU-MIMO
  //      dual-band, 400 Mbps @ 2.4 GHz + 867 Mbps @ 5 GHz. Internal
  //      omni-directional antennas (8 dBi @ 2.4 GHz, 9 dBi @ 5 GHz).
  //      256 users, 16 SSIDs, 1× GbE PoE-in (802.3af/at), IP67 UV-
  //      rated outdoor housing with integrated cold-start heater,
  //      ESD protection, LTE-resilience filtering, rail-shock +
  //      vibration certified. Cloud-managed via cnMaestro.
  {
    id: 'cambium-cnpilot-e510-outdoor',
    manufacturer: 'Cambium',
    model: 'cnPilot e510 Outdoor',
    category: 'wireless-access-point',
    tagline:
      'cnPilot e510 — Wi-Fi 5 outdoor rugged, 8/9 dBi omni, 256 users, IP67 + rail-shock cert',
    verified: true,
    datasheetUrl:
      'https://www.cambiumnetworks.com/products/wifi/cnpilot-e510-wifi-access-point/',
    specs: {
      wifiStandard: 'wifi5-wave2',
      bands: ['2.4 GHz', '5 GHz'],
      throughputMbps: 1267, // 867 + 400
      spatialStreams: '2+2',
      muMimoOfdma: true,
      lanPorts: 0,
      uplinkSpeed: '1G',
      poeIn: '802.3at',
      outdoor: true,
      ipRating: 'IP67',
      operatingTempC: '-40..+65',
      maxClients: 256,
      cloudPlatform: 'cnMaestro',
      clientCoverageM: 100,
      // SRC: https://brandcentral.cambiumnetworks.com/m/55cbbf172867113c/original/Cambium_Networks_data_sheet_cnPilot_e505_e510_e700_family.pdf
      //      (family datasheet, e510 column, read 2026-08-25). Internal omni
      //      8 dBi @ 2.4 GHz, 9 dBi @ 5 GHz. Max EIRP 32 dBm @ 2.4 GHz,
      //      34.5 dBm @ 5 GHz, "EIRP limited by country regulations", so the
      //      propagation model clamps to the active regulatory cap. WLAN:
      //      256 clients per radio.
      antennaGain24Dbi: 8,
      antennaGain5Dbi: 9,
      eirp24Dbm: 32,
      eirp5Dbm: 34.5,
      maxClientsPerRadio: 256,
      // "Internal omni-directional" per the family datasheet antenna row.
      antennaPattern: 'omni',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // MikroTik — budget outdoor / SMB (dominant in EU installer channel)
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://mikrotik.com/product/wap_ax (official product page, read
  //      2026-08-25) — dual-band dual-chain (2x2 MIMO) Wi-Fi 6 outdoor box,
  //      2x GbE (one PoE-in, 802.3af/at or 12-57V DC), IP54, -40..+70 C,
  //      $89 street price. TX power up to 24 dBm @ 2.4 GHz / 23 dBm @ 5 GHz;
  //      antenna gain 6.9 dBi @ 2.4 GHz / 7 dBi @ 5 GHz.
  //      ⚠️ Antenna PATTERN not stated on this page (no polar chart
  //      published, unlike the Netgear/EnGenius/Ruckus sheets below) — left
  //      unset rather than guessed; the model then radiates isotropically at
  //      peak gain, same as every other unclassified AP in this catalog.
  {
    id: 'mikrotik-wap-ax',
    manufacturer: 'MikroTik',
    model: 'wAP ax',
    category: 'wireless-access-point',
    tagline: 'wAP ax — Wi-Fi 6 outdoor, IP54, -40..+70 °C, 2x GbE PoE, budget EU-channel staple',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/wap_ax',
    specs: {
      wifiStandard: 'wifi6',
      bands: ['2.4 GHz', '5 GHz'],
      throughputMbps: 2974, // 574 + 2400
      spatialStreams: '2+2',
      lanPorts: 1, // 2x GbE total, one doubles as PoE-in uplink
      uplinkSpeed: '1G',
      poeIn: '802.3at',
      outdoor: true,
      ipRating: 'IP54',
      operatingTempC: '-40..+70',
      txPower24Dbm: 24,
      txPower5Dbm: 23,
      antennaGain24Dbi: 6.9,
      antennaGain5Dbi: 7,
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Ruckus (CommScope) — enterprise high-density outdoor
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://webresources.ruckuswireless.com/datasheets/t350/ds-ruckus-t350.pdf
  //      (official CommScope datasheet, read 2026-08-25 as rendered pages —
  //      text-layer PDF extraction failed, confirmed via the RF spec table
  //      image). T350SE variant: internal 120° SECTOR antenna (+ N-type
  //      external connectors), confirmed by BOTH the antenna-type row AND
  //      the printed azimuth/elevation polar plots (narrow forward lobe,
  //      not a circle) — the clearest sectorized confirmation in this
  //      catalog. Gain 6 dBi @ 2.4 GHz / 8 dBi @ 5 GHz. "Peak Transmit
  //      Power (Tx port/chain + 3 dB combining gain)" 26 dBm @ 2.4 GHz /
  //      25 dBm @ 5 GHz — a conducted figure BEFORE antenna gain, same
  //      convention as this catalog's txPowerDbm field. Up to 512 clients,
  //      IP-67, pole/wall mount, 802.3at Class 4.
  {
    id: 'ruckus-t350se',
    manufacturer: 'Ruckus (CommScope)',
    model: 'T350SE',
    category: 'wireless-access-point',
    tagline: 'T350SE — Wi-Fi 6 outdoor, 120° internal sector + external antenna capable, IP-67, 512 clients',
    verified: true,
    datasheetUrl: 'https://webresources.ruckuswireless.com/datasheets/t350/ds-ruckus-t350.pdf',
    specs: {
      wifiStandard: 'wifi6',
      bands: ['2.4 GHz', '5 GHz'],
      throughputMbps: 1774, // printed peak PHY: 574 @ 2.4 + 1200 @ 5
      spatialStreams: '2+2',
      muMimoOfdma: true,
      lanPorts: 0,
      uplinkSpeed: '1G',
      poeIn: '802.3at',
      outdoor: true,
      ipRating: 'IP-67',
      operatingTempC: '-40..+65',
      maxClients: 512,
      cloudPlatform: 'RUCKUS Cloud',
      txPower24Dbm: 26,
      txPower5Dbm: 25,
      antennaGain24Dbi: 6,
      antennaGain5Dbi: 8,
      antennaPattern: 'semi-directional',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Netgear Insight — SMB indoor Wi-Fi 6
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.downloads.netgear.com/files/GDC/WAX620/WAX620_DS.pdf
  //      (official datasheet, read 2026-08-25 as rendered pages). Antenna
  //      gain "Internal 3.1/4.3 dBi (2.4GHz/5GHz)" from the product-
  //      comparison table; the printed XY-plane radiation pattern is a full
  //      circle (confirms omni). ⚠️ This datasheet does NOT publish a
  //      conducted TX power figure anywhere in its 7 pages — txPowerDbm and
  //      eirpDbm are deliberately left unset (nothing to report), so the
  //      propagation model falls back to the regulatory cap while the real
  //      gain still shapes the omni vertical pattern. Indoor only (0..40 C,
  //      "intended for indoor use only" per the warranty note), 2.5GbE PoE+
  //      uplink, ceiling/wall mount.
  {
    id: 'netgear-wax620',
    manufacturer: 'Netgear',
    model: 'WAX620 Insight Managed',
    category: 'wireless-access-point',
    tagline: 'WAX620 — Wi-Fi 6 AX3600 indoor, 2.5GbE PoE+, 4x4 omni, Insight cloud managed',
    verified: true,
    datasheetUrl: 'https://www.downloads.netgear.com/files/GDC/WAX620/WAX620_DS.pdf',
    specs: {
      wifiStandard: 'wifi6',
      bands: ['2.4 GHz', '5 GHz'],
      throughputMbps: 3600, // 1200 @ 2.4 + 2400 @ 5, printed AX3600 class
      spatialStreams: '4+4',
      muMimoOfdma: true,
      lanPorts: 0,
      uplinkSpeed: '2.5G',
      poeIn: '802.3at',
      outdoor: false,
      operatingTempC: '0..+40',
      cloudPlatform: 'NETGEAR Insight',
      antennaGain24Dbi: 3.1,
      antennaGain5Dbi: 4.3,
      antennaPattern: 'omni',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // EnGenius — cloud-managed security AP (WIDS/WIPS)
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.engeniustech.com/wp-content/uploads/2021/11/ECW230S-Datasheet-v1.pdf
  //      (official datasheet, read 2026-08-25 as rendered pages). Antenna
  //      row states "Integrated Omni-Directional Antenna" explicitly, gain
  //      4x 2.4 GHz: 5 dBi / 4x 5 GHz: 6 dBi; confirmed omni by the printed
  //      YZ/XY polar gain charts too. "Transmit Power: Up to 23 dBm on
  //      2.4 GHz / Up to 23 dBm on 5 GHz (maximum power is limited by
  //      regulatory domain)" — a global-max figure, clamped like every
  //      other entry's txPowerDbm. The "S" suffix model adds a dedicated
  //      scanning radio for WIDS/WIPS + rogue-AP detection (AirGuard) —
  //      relevant positioning for a CCTV/security planning tool. Indoor
  //      ceiling AP, 2.5GbE + 802.3at, 8 SSIDs/band. No max-client figure
  //      is published in the datasheet, so maxClients is left unset.
  {
    id: 'engenius-ecw230s',
    manufacturer: 'EnGenius',
    model: 'ECW230S',
    category: 'wireless-access-point',
    tagline: 'ECW230S — Wi-Fi 6 4x4 indoor security AP, WIDS/WIPS + rogue-AP detection, 2.5GbE',
    verified: true,
    datasheetUrl: 'https://www.engeniustech.com/wp-content/uploads/2021/11/ECW230S-Datasheet-v1.pdf',
    specs: {
      wifiStandard: 'wifi6',
      bands: ['2.4 GHz', '5 GHz'],
      throughputMbps: 3548, // printed SU-MIMO ceiling: 1148 @ 2.4 + 2400 @ 5
      spatialStreams: '4+4',
      muMimoOfdma: true,
      lanPorts: 0,
      uplinkSpeed: '2.5G',
      poeIn: '802.3at',
      outdoor: false,
      operatingTempC: '0..+40',
      cloudPlatform: 'EnGenius Cloud',
      txPower24Dbm: 23,
      txPower5Dbm: 23,
      antennaGain24Dbi: 5,
      antennaGain5Dbi: 6,
      antennaPattern: 'omni',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Ubiquiti UniFi — second SKU (long-range, high-power)
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://techspecs.ui.com/unifi/wifi/u6-lr (official tech-specs page,
  //      read 2026-08-25, same authoritative source pattern as U6 Mesh).
  //      TX power 26 dBm both bands; antenna gain 4 dBi @ 2.4 GHz, 5.5 dBi
  //      @ 5 GHz; 350+ concurrent clients; IP54; ceiling/wall mount;
  //      -30..+60 °C.
  {
    id: 'ubiquiti-u6-lr',
    manufacturer: 'Ubiquiti',
    model: 'UniFi U6 Long-Range',
    category: 'wireless-access-point',
    tagline: 'U6-LR — Wi-Fi 6 high-power long-range, 350+ clients, IP54 ceiling/wall',
    verified: true,
    datasheetUrl: 'https://dl-origin.ubnt.com/ds/u6-lr_ds.pdf',
    specs: {
      wifiStandard: 'wifi6',
      bands: ['2.4 GHz', '5 GHz'],
      throughputMbps: 4800, // 5 GHz headline figure (4x4); 2.4 GHz adds ~600 Mbps class
      spatialStreams: '2+4',
      muMimoOfdma: true,
      lanPorts: 0,
      uplinkSpeed: '1G',
      poeIn: '802.3at',
      outdoor: true,
      ipRating: 'IP54',
      operatingTempC: '-30..+60',
      maxClients: 350,
      cloudPlatform: 'UniFi Network',
      txPower24Dbm: 26,
      txPower5Dbm: 26,
      antennaGain24Dbi: 4,
      antennaGain5Dbi: 5.5,
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // TP-Link Omada — second SKU (indoor ceiling, AX5400)
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://static.tp-link.com/upload/product-overview/2025/202509/20250922/Datasheet_EAP670%202.20.pdf
  //      (official datasheet, read 2026-08-25 as rendered pages). Antenna
  //      row states "internal omnidirectional antennas" explicitly for
  //      BOTH bands: 2.4 GHz 2x4 dBi peak gain, 5 GHz 4x5 dBi peak gain
  //      ("gains above are single-antenna peak gains"). Tx Power table, CE
  //      (EIRP): 2.4 GHz 20 dBm; 5 GHz 23 dBm (U-NII-1/2A) or 30 dBm
  //      (U-NII-2C, requires DFS) — stored as the highest published ceiling
  //      (30 dBm), same convention as the EAP650-Outdoor entry above; the
  //      propagation model's EU cap clamps it regardless. 250+ clients,
  //      2.5GbE PoE+ port, indoor ceiling mount (no IP rating stated).
  {
    id: 'tplink-eap670',
    manufacturer: 'TP-Link',
    model: 'Omada EAP670',
    category: 'wireless-access-point',
    tagline: 'EAP670 — Wi-Fi 6 AX5400 indoor ceiling, 2.5GbE PoE+, 250+ clients, Omada SDN',
    verified: true,
    datasheetUrl: 'https://static.tp-link.com/upload/product-overview/2025/202509/20250922/Datasheet_EAP670%202.20.pdf',
    specs: {
      wifiStandard: 'wifi6',
      bands: ['2.4 GHz', '5 GHz'],
      throughputMbps: 5378, // 574 + 4804, printed max data rates
      spatialStreams: '2+4',
      muMimoOfdma: true,
      lanPorts: 0,
      uplinkSpeed: '2.5G',
      poeIn: '802.3at',
      outdoor: false,
      maxClients: 250,
      meshCapable: true,
      cloudPlatform: 'Omada SDN',
      antennaGain24Dbi: 4,
      antennaGain5Dbi: 5,
      eirp24Dbm: 20,
      eirp5Dbm: 30,
      antennaPattern: 'omni',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Grandstream — indoor Wi-Fi 5 wave-2, VoWiFi / IP-phone ecosystem
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.grandstream.com/hubfs/Product_Documentation/datasheet_gwn7630_english.pdf
  //      (official datasheet, read 2026-08-25 as rendered pages). Antenna
  //      row: "4 dual band internal antennas, 2.4 GHz gain 4 dBi, 5 GHz
  //      gain 5 dBi" — NOT explicitly labelled omni/directional in this
  //      sheet (unlike the EnGenius/Netgear/TP-Link/Ruijie sheets above),
  //      so antennaPattern is deliberately left unset rather than assumed.
  //      "Maximum TX Power: 2.4G 27 dBm / 5G 25 dBm (varies by country,
  //      frequency band and MCS rate)". 200+ clients, PoE 802.3at, indoor
  //      wall/ceiling mount, 0..40 °C. Wi-Fi 5 Wave-2 (802.11ac), not
  //      Wi-Fi 6 — Grandstream's install base is mostly VoWiFi/IP-phone
  //      deployments where this model still ships new.
  {
    id: 'grandstream-gwn7630',
    manufacturer: 'Grandstream',
    model: 'GWN7630',
    category: 'wireless-access-point',
    tagline: 'GWN7630 — Wi-Fi 5 Wave-2 4x4:4 indoor, 200+ clients, GWN.Cloud, VoWiFi-friendly',
    verified: true,
    datasheetUrl: 'https://www.grandstream.com/hubfs/Product_Documentation/datasheet_gwn7630_english.pdf',
    specs: {
      wifiStandard: 'wifi5-wave2',
      bands: ['2.4 GHz', '5 GHz'],
      throughputMbps: 2533, // 800 (2.4 GHz, 256-QAM) + 1733 (5 GHz)
      spatialStreams: '4+4',
      muMimoOfdma: true,
      lanPorts: 1,
      uplinkSpeed: '1G',
      poeIn: '802.3at',
      outdoor: false,
      operatingTempC: '0..+40',
      maxClients: 200,
      cloudPlatform: 'GWN.Cloud',
      txPower24Dbm: 27,
      txPower5Dbm: 25,
      antennaGain24Dbi: 4,
      antennaGain5Dbi: 5,
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Ruijie Reyee — budget-outdoor SMB (China-dominant, growing EU channel)
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: official Ruijie Reyee datasheet PDF (mirrored copy read 2026-08-25
  //      as rendered pages; original https://reyee.ruijie.com/en-global/resources/preview/RY-77054/
  //      gates the PDF behind a JS download button that WebFetch can't
  //      follow, so the datasheetUrl below points at the distributor mirror
  //      actually read). "UFO" spherical outdoor body, "2 built-in
  //      omnidirectional antennas" per band, 3.00 dBi @ 2.4 GHz, 3.00 dBi
  //      @ 5 GHz. Maximum transmit power table gives BOTH the global
  //      conducted figure (5 GHz combined 28 dBm / 2.4 GHz combined 29 dBm)
  //      AND a full regional EIRP table; EU & UK row: 2.4 GHz EIRP ≤20 dBm,
  //      5 GHz (5470-5725 MHz) EIRP ≤30 dBm — stored as eirp24/5Dbm,
  //      matching the TP-Link entries' convention of the highest published
  //      EU ceiling (the propagation model's own cap clamps further where
  //      relevant). 256 max clients, PoE+ (802.3at), IP68, -30..+65 °C,
  //      wall/pole mount, AP mesh capable.
  {
    id: 'ruijie-reyee-rg-rap6262g',
    manufacturer: 'Ruijie Reyee',
    model: 'RG-RAP6262(G)',
    category: 'wireless-access-point',
    tagline: 'RG-RAP6262(G) — Wi-Fi 6 AX1800 outdoor omni "UFO", IP68, 256 clients, budget SMB',
    verified: true,
    datasheetUrl: 'https://www.eos.com.au/pub/media/doc/networkswitch/RG-RAP6262(G)_Datasheet_Official_2025-10-11.pdf',
    specs: {
      wifiStandard: 'wifi6',
      bands: ['2.4 GHz', '5 GHz'],
      throughputMbps: 1774, // printed "Maximum wireless data rate"
      spatialStreams: '2+2',
      muMimoOfdma: true,
      lanPorts: 1,
      uplinkSpeed: '1G',
      poeIn: '802.3at',
      outdoor: true,
      ipRating: 'IP68',
      operatingTempC: '-30..+65',
      maxClients: 256,
      meshCapable: true,
      cloudPlatform: 'Reyee Cloud',
      antennaGain24Dbi: 3,
      antennaGain5Dbi: 3,
      eirp24Dbm: 20,
      eirp5Dbm: 30,
      antennaPattern: 'omni',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Teltonika Networks — Lithuanian, dominant in EU industrial/RMS-managed gear
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://eshop.sectron.eu/UserFiles/File/Teltonika_TAP400_datasheet.pdf
  //      (official Teltonika Networks datasheet v1.00, 2026-01-15, read
  //      2026-08-25 as rendered pages). Distinct from Teltonika's RUTX
  //      cellular-router line (which has Wi-Fi as a secondary radio, not a
  //      dedicated AP) — TAP400 is their first Wi-Fi 6 AP-only product.
  //      "3x Internal for 2.4 GHz Wi-Fi and 5 GHz Wi-Fi antennas" /
  //      "3x 2400-2500 MHz, peak gain <4.33 dBi & 5150-5850 MHz, peak gain
  //      <4.9 dBi, omnidirectional" — explicit omni confirmation. ⚠️ No
  //      transmit power in dBm is published anywhere in the 6-page sheet
  //      (only the antenna gain above and the EN 300 328 / EN 301 893
  //      compliance listing) — same gap as the Netgear WAX620 entry above,
  //      handled the same way: txPowerDbm/eirpDbm left unset so the model
  //      falls back to the regulatory cap while the real gain still shapes
  //      the omni vertical pattern. 512 simultaneous connections, PoE
  //      802.3af Class 3 (lower budget than most Wi-Fi 6 APs in this
  //      catalog — a real differentiator, not an omission), single
  //      2.5GbE RJ45, ceiling mount, IP30 (indoor), 0..50 °C, mesh
  //      (802.11s) + fast roaming (802.11r), managed via Teltonika RMS.
  {
    id: 'teltonika-tap400',
    manufacturer: 'Teltonika Networks',
    model: 'TAP400',
    category: 'wireless-access-point',
    tagline: 'TAP400 — Wi-Fi 6 indoor ceiling AP, 512 clients, RMS managed, mesh + fast roaming',
    verified: true,
    datasheetUrl: 'https://eshop.sectron.eu/UserFiles/File/Teltonika_TAP400_datasheet.pdf',
    specs: {
      wifiStandard: 'wifi6',
      bands: ['2.4 GHz', '5 GHz'],
      throughputMbps: 2978, // 576 + 2402, printed max data rates
      muMimoOfdma: true,
      lanPorts: 0,
      uplinkSpeed: '2.5G',
      poeIn: '802.3af',
      outdoor: false,
      ipRating: 'IP30',
      operatingTempC: '0..+50',
      maxClients: 512,
      meshCapable: true,
      cloudPlatform: 'Teltonika RMS',
      antennaGain24Dbi: 4.33,
      antennaGain5Dbi: 4.9,
      antennaPattern: 'omni',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // GENERIC — representative presets, NOT a producer SKU (verified: false).
  // Backs the three "Generic Wi-Fi …" quick-pick tiles in the unified
  // equipment picker (UnifiedEquipmentCatalogDialog QUICK_PICKS.wireless-ap)
  // — ids below are read directly by that preset list, so keep them in
  // sync. Numbers are typical/representative, the same posture as the
  // camera generics (Generic Dome/Bullet/PTZ/Panoramic): derived as
  // round mid-points across the VERIFIED entries above, not read off any
  // one datasheet. The three presets deliberately carry DIFFERENT antenna
  // assumptions so picking one is a real physics choice, not a label:
  // indoor omni (ceiling-mount convention), outdoor UNKNOWN pattern
  // (conservative — real outdoor bodies split between omni and sector, so
  // asserting either would be a coin flip), outdoor omni EXPLICIT for the
  // high-density-campus tile. None assert a TX power or EIRP figure — the
  // propagation model falls back to the regulatory cap, which is the
  // honest choice for a device with no real datasheet behind it.
  {
    id: 'gen-wifi-indoor',
    manufacturer: 'Generic',
    model: 'Wi-Fi 6 indoor AP',
    category: 'wireless-access-point',
    tagline: 'Generic Wi-Fi 6 indoor AP — 2×2 dual-band, PoE+ (representative, not a producer SKU)',
    verified: false,
    specs: {
      wifiStandard: 'wifi6',
      bands: ['2.4 GHz', '5 GHz'],
      spatialStreams: '2+2',
      muMimoOfdma: true,
      poeIn: '802.3at',
      outdoor: false,
      antennaGain24Dbi: 3,
      antennaGain5Dbi: 3,
      antennaPattern: 'omni',
    },
  },
  {
    id: 'gen-wifi-outdoor',
    manufacturer: 'Generic',
    model: 'Wi-Fi 6 outdoor AP',
    category: 'wireless-access-point',
    tagline: 'Generic Wi-Fi 6 outdoor AP — IP67, mesh-capable (representative, not a producer SKU)',
    verified: false,
    specs: {
      wifiStandard: 'wifi6',
      bands: ['2.4 GHz', '5 GHz'],
      spatialStreams: '2+2',
      muMimoOfdma: true,
      poeIn: '802.3at',
      outdoor: true,
      ipRating: 'IP67',
      meshCapable: true,
      // No antennaGain / antennaPattern: real outdoor bodies split roughly
      // evenly between omni pucks and sector panels, so this tier stays
      // isotropic-at-cap rather than assume either.
    },
  },
  {
    id: 'gen-wifi-omni',
    manufacturer: 'Generic',
    model: 'Wi-Fi 6 outdoor omni',
    category: 'wireless-access-point',
    tagline: 'Generic outdoor omni — high-density campus (representative, not a producer SKU)',
    verified: false,
    specs: {
      wifiStandard: 'wifi6',
      bands: ['2.4 GHz', '5 GHz'],
      spatialStreams: '2+2',
      muMimoOfdma: true,
      poeIn: '802.3at',
      outdoor: true,
      ipRating: 'IP67',
      meshCapable: true,
      antennaGain24Dbi: 6,
      antennaGain5Dbi: 7,
      antennaPattern: 'omni',
    },
  },
];
